import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { Dialog } from '@/components/AppDialog'
import { useHalls, usePostponeOrder } from '@/api/hooks'
import type { OrderDetail } from '@/api/types'
import { ErrorBanner, formatDate, isoDate } from '@/components/common'

/**
 * Moves a booking to another day, at the customer's asking.
 *
 * <p>Every sitting travels together and keeps the hours it was agreed at, and
 * each hall is checked free on the new date before anything moves. A clash
 * comes back as a refusal naming what is in the way, rather than a double
 * booking somebody discovers on the morning.</p>
 */
export function PostponeDialog({
  order,
  onClose,
}: {
  order: OrderDetail | null
  onClose: () => void
}) {
  const postpone = usePostponeOrder()
  const { data: halls } = useHalls()
  const [eventDate, setEventDate] = useState('')
  const [reason, setReason] = useState('')
  // A sitting keeps the slot it holds unless the office picks another; the
  // hours then come from that slot rather than being carried across.
  const [slots, setSlots] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!order) return
    setEventDate('')
    setReason('')
    setSlots({})
  }, [order?.id])

  if (!order) return null

  const tomorrow = isoDate(1)
  const valid = Boolean(eventDate) && eventDate > isoDate(0) && Boolean(reason.trim())

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change the date for {order.orderNumber}</DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={postpone.error} />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Booked for {formatDate(order.eventDate)} · {order.sessions.length}{' '}
          {order.sessions.length === 1 ? 'session' : 'sessions'} · {order.customerName}
        </Typography>

        <Stack spacing={2}>
          <TextField
            label="New event date"
            type="date"
            value={eventDate}
            onChange={(event) => setEventDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: tomorrow }}
            required
            fullWidth
            helperText="Every session moves by the same number of days and keeps its hours"
          />
          <TextField
            label="Why it is being moved"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            required
            fullWidth
            placeholder="Customer asked to postpone - family bereavement"
          />
        </Stack>

        {/* A postponed booking often wants a different part of the day as
            well - a lunch that becomes a dinner. Offered per session, because
            a booking with a breakfast and a lunch does not move both into the
            same slot. */}
        {order.sessions.filter((sitting) => sitting.hallId).length > 0 ? (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Slots on the new date</Typography>
            {order.sessions
              .filter((sitting) => sitting.hallId)
              .map((sitting) => {
                const hall = halls?.find((candidate) => candidate.id === sitting.hallId)
                return (
                  <TextField
                    key={sitting.id}
                    select
                    fullWidth
                    label={`${sitting.eventName ?? sitting.mealPeriod ?? 'Sitting'} — ${hall?.name ?? 'hall'}`}
                    value={slots[sitting.id] ?? ''}
                    onChange={(event) =>
                      setSlots((current) => ({ ...current, [sitting.id]: event.target.value }))
                    }
                    helperText="Leave as it is to keep the hours it was agreed at"
                  >
                    <MenuItem value="">Keep the same hours</MenuItem>
                    {(hall?.timeSlots ?? []).map((slot) => (
                      <MenuItem key={slot.id} value={slot.id}>
                        {slot.name} · {slot.startTime?.slice(0, 5)}–{slot.endTime?.slice(0, 5)}
                      </MenuItem>
                    ))}
                  </TextField>
                )
              })}
          </Stack>
        ) : null}

        {/* Said before it happens, not discovered afterwards. */}
        <Alert severity="info" sx={{ mt: 2 }}>
          Each hall is checked free on the new date first; if one is taken, nothing moves. Kitchen
          requests, rosters and trips already raised stay on the old dates — they are a plan for a
          day that is no longer happening, and need looking at again.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!valid || postpone.isPending}
          onClick={() =>
            postpone.mutate(
              {
                orderId: order.id,
                body: {
                  eventDate,
                  reason: reason.trim(),
                  slots: Object.entries(slots)
                    .filter(([, slotId]) => slotId)
                    .map(([sessionId, hallSlotId]) => ({ sessionId, hallSlotId })),
                },
              },
              { onSuccess: onClose },
            )
          }
        >
          Change the date
        </Button>
      </DialogActions>
    </Dialog>
  )
}
